// mortgageRecFormSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  LenderRecommendationType,
  RecommendationFormValues,
} from '@/pages/mortgage-workflow/routes/Recommendation/types/recommendation.types';
import { RootState } from '@/store'; // adjust the path

const initialState: any = {
  firstName: '',
  lastName: '',
  creditLiscense: '',
  creditRepresentative: '',
  date: null,
  servicePackage: [],

  //Purchase Price
  purchasePrice: 10000,

  // Funds comments
  fundsComments: '',

  // Funds Required (LVR for purchase)
  lvrOption1: 60,
  lvrOption2: 70,
  lvrOption3: 80,
  lvrOption4: 90,

  // Existing property
  existingPropertyComments: '',
  existingLvrOption1: 50,
  existingLvrOption2: 55,
  existingLvrOption3: 60,
  existingLvrOption4: 70,

  // Loan structure
  loanStructureComments: '',

  // Estimated value scenarios
  purchasePropertyLocation: '',
  scenario1RepaymentType: 'P_AND_I',
  scenario2RepaymentType: 'P_AND_I',
  scenario3RepaymentType: 'P_AND_I',
  scenario4RepaymentType: 'P_AND_I',

  scenario1InterestType: 'VARIABLE',
  scenario2InterestType: 'VARIABLE',
  scenario3InterestType: 'VARIABLE',
  scenario4InterestType: 'VARIABLE',

  scenario1InterestRate: 6.09,
  scenario2InterestRate: 6.14,
  scenario3InterestRate: 6.19,
  scenario4InterestRate: 6.79,

  scenario1TermYears: 30,
  scenario2TermYears: 30,
  scenario3TermYears: 30,
  scenario4TermYears: 30,

  estimatedValueCombinedComments: '',

  combinedScenario1RepaymentType: 'P_AND_I',
  combinedScenario2RepaymentType: 'P_AND_I',
  combinedScenario3RepaymentType: 'P_AND_I',
  combinedScenario4RepaymentType: 'P_AND_I',

  combinedScenario1InterestType: 'VARIABLE',
  combinedScenario2InterestType: 'VARIABLE',
  combinedScenario3InterestType: 'VARIABLE',
  combinedScenario4InterestType: 'VARIABLE',

  combinedScenario1InterestRate: 6.09,
  combinedScenario2InterestRate: 6.14,
  combinedScenario3InterestRate: 6.19,
  combinedScenario4InterestRate: 6.79, // or 6.779

  combinedScenario1TermYears: 30,
  combinedScenario2TermYears: 30,
  combinedScenario3TermYears: 30,
  combinedScenario4TermYears: 30,

  lenders: [],

  // Tax
  miscCost: 1000,
  buyerAgentFee: 15000,
  legalFeesUsd: 1500,
  legalFeesAud: 0,
  fxRateUsdToAud: undefined,

  loanToValueRatio: 80,
  mortgageInterestRate: 5.59,
  loanTermYears: 30,
  repaymentType: 'P_AND_I',
  year1InterestApprox: 0,
  year1PrincipalReductionApprox: 0, // 🔹 NEW
  loanBalanceAfterYear1Approx: 0, // 🔹 NEW

  rentIncomeWeek: 0,
  rentIncomeMonth: 0,
  rentIncomeAnnual: 0,
  totalPropertyExpensesAnnual: 0,
  netRentalIncomeAnnual: 0,
  netRentalYieldPercent: 0,
  annualMortgageRepayments: 0,
  annualCashFlowPosition: 0,

  taxResidencyStatus: 'Non-Resident',
  buildingDepreciation: 0,
  plantEquipmentDepreciation: 0,
  totalDepreciation: 0,
  otherAnnualCosts: 3000,
  interestDeductionAnnual: 0,
  netRentalIncomeBeforeDepreciation: 0,
  taxableIncomeAfterDepreciation: 0,
  incomeTaxPayable: 0,
};

type FieldName = keyof RecommendationFormValues;

export type UpdateFieldPayload = {
  name: FieldName;
  value: RecommendationFormValues[FieldName];
};

const mortgageRecFormSlice = createSlice({
  name: 'mortgageRecSlice',
  initialState,
  reducers: {
    setRecFromField: (
      state,
      action: PayloadAction<{
        field: keyof RecommendationFormValues;
        value: any;
      }>,
    ) => {
      const { field, value } = action.payload;

      state[field] = value;
    },
    setForm: (_state, action: PayloadAction<RecommendationFormValues>) =>
      action.payload,
    patchForm: (
      state,
      action: PayloadAction<Partial<RecommendationFormValues>>,
    ) => {
      state = { ...state, ...action.payload };
    },
    updateField: (state, { payload }: PayloadAction<UpdateFieldPayload>) => {
      const { name, value } = payload;

      // tiny escape hatch; Immer will still track this
      (state as any)[name] = value;
    },
    //  Add full lender list after sorting or selection
    setLenders: (state, action: PayloadAction<LenderRecommendationType[]>) => {
      state.lenders = action.payload;
    },

    //  Update individual lender
    updateLender: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof LenderRecommendationType;
        value: string | number;
      }>,
    ) => {
      const { index, field, value } = action.payload;
      const draft = state as any;
      draft.lenders[index][field] = value as any;
    },

    //  Optional — Add lender
    addLender: (state, action: PayloadAction<LenderRecommendationType>) => {
      state.lenders.push(action.payload);
    },

    //  Optional — Remove lender
    removeLender: (state, action: PayloadAction<number>) => {
      state.lenders.splice(action.payload, 1);
    },
    resetForm: () => initialState,
  },
});

export const {
  setForm,
  patchForm,
  updateField,
  resetForm,
  setLenders,
  updateLender,
  addLender,
  removeLender,
  setRecFromField,
} = mortgageRecFormSlice.actions;

export const selectMortgageRecForm = (state: RootState) =>
  state.mortgageRecSlice;

export default mortgageRecFormSlice.reducer;
